import { CollectionConst } from "constants/document/CollectionConst";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { EMPTY_REFUND_ENTITY, RefundEntity } from "domain/entity/Refund/RefundEntity";
import { RefundOperationCriteria } from "domain/entity/Refund/RefundOperationCriteria";
import { EMPTY_REFUND_SEARCH_CRITERIA, RefundSearchCriteria } from "domain/entity/Refund/RefundSearchCriteria";
import { BankerRepository } from "domain/repository/Banker/BankerRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import { RefundRepository } from "domain/repository/Refund/RefundRepo";
import _ from "lodash";
import { refundStatusDropdownOption, refundTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { ParameterCodeValue } from "presentation/constant/Parameter/ParameterCodeValue";
import { RefundPermissionCodePolicy } from "presentation/constant/Refund/RefundConstant";
import { createRefundSaptValidationSchema, createRefundValidationSchema } from "presentation/constant/Refund/RefundValidationSchema";
import { Validation } from "presentation/constant/Validation";
import { RefundModel } from "presentation/model/Refund/RefundModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface RefundVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<RefundModel>> | ((value: SetStateAction<RefundModel>) => void),
    ],
    repo: RefundRepository,
    companyRepo: CompanyRepository,
    bankerRepo: BankerRepository,
    parameterDetailRepo: ParameterDetailRepository,
}

export const RefundVM = ({ dispatch, repo, companyRepo, bankerRepo, parameterDetailRepo }: RefundVMProps) => {
    const [refundMainDispatch] = dispatch;

    const loadDropdownOption = async () => {

        refundMainDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                statusDropdownOptions: refundStatusDropdownOption,
                refundTypeDropdownOptions: refundTypeDropdownOption
            }
        }))

        await companyRepo.getAllCompanyForCombobox().then(
            companies => {
                let newCompanies = companies.filter(entity => entity.customerType !== 'SL' && entity.companyType !== 'MASTER' && entity.companyType !== 'ALLIANCE');
                let companyCodeDropdownOption = newCompanies?.map((company) => ({
                    dropdownLabel: company.companyCode,
                    tagLabel: company.companyCode,
                    value: company.companyCode,
                })) ?? []
                companyCodeDropdownOption = _.orderBy(companyCodeDropdownOption, "dropdownLabel");

                refundMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        companyCodeDropdownOptions: companyCodeDropdownOption,

                    }
                }))
            }
        );

        await bankerRepo.getEntities().then(
            bankers => {
                let newBankers = bankers.filter(entity => entity.activeInd === 'Y');
                let bankCodeDropdownOption = newBankers?.map((banker) => ({
                    dropdownLabel: banker.bankCode + " " + banker.bankName,
                    tagLabel: banker.bankCode + " " + banker.bankName,
                    value: banker.bankCode + " " + banker.bankName,
                })) ?? []
                bankCodeDropdownOption = _.orderBy(bankCodeDropdownOption, "dropdownLabel");

                refundMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        bankCodeDropdownOptions: bankCodeDropdownOption,
                    }
                }))
            }
        );

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeValue.REFUND_REJECT_REASON_CODE).then(
            reasonCodes => {
                const reasonCodeDropdownOption = reasonCodes?.map((reasonCode) => ({
                    dropdownLabel: reasonCode.parameterDtlCode,
                    tagLabel: reasonCode.parameterDtlCode,
                    value: reasonCode.parameterDtlCode,
                })) ?? []

                refundMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        reasonCodeDropdownOptions: reasonCodeDropdownOption,
                    }
                }))
            }
        )

    }

    const onSearch = async (criteria: RefundSearchCriteria) => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                // refundList: [],
                searchCriteria: criteria,
                isShowEditPanel: false,
                isShowOperationPanel: false,
                isAdd: false,
                isEdit: false,
                reasonCode: "",
                bankerIdAndBankerAcNo: "",
                currentEditRow: { ...EMPTY_REFUND_ENTITY }
            }
        });
        await repo.onSearch(criteria).then((data) => {
            refundMainDispatch(prevState => {
                return {
                    ...prevState,
                    refundList: data,
                }
            });
        });
    }

    const onOperationFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                [fieldKey]: val,
            }
        })
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onEditFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (fieldKey === 'receiptNo') {
            val = val ? val.toUpperCase() : null;
        }
        refundMainDispatch(prevState => {
            if (fieldKey === 'cnic') {
                if (val) {
                    if (!(val.match(/^[\d-]+$/) !== null)) {
                        val = prevState.currentEditRow[fieldKey];
                    }
                }
            }
            return {
                ...prevState,
                currentEditRow: {
                    ...prevState.currentEditRow,
                    [fieldKey]: val,
                },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: ''
                    },
                }
            }
        })
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        refundMainDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }


    const onRemoveAllSearchCriteria = async () => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: { ...EMPTY_REFUND_SEARCH_CRITERIA }
            }
        })
    }


    const onRowDoubleClick = async (entity: RefundEntity) => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: {
                    ...entity,
                    chequeAmount: entity.status === CollectionConst.refundStatus.ITEM_PROCESSED ? entity.refundAmount : entity.chequeAmount,
                },
                currentRow: {
                    ...entity,
                    chequeAmount: entity.status === CollectionConst.refundStatus.ITEM_PROCESSED ? entity.refundAmount : entity.chequeAmount,
                },
                isShowEditPanel: true,
            }
        })
    }

    const updateSelectedRows = async (rows: RefundEntity[]) => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: rows,
                forceRefresh: !prevState.forceRefresh,
            }
        })
    }

    const onSearchClick = async () => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isShowSearchPanel: !prevState.isShowSearchPanel,
            }
        })
    }

    const onCloseEditPanel = async () => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: false,
                isEdit: false,
                isAdd: false,
                currentEditRow: { ...EMPTY_REFUND_ENTITY },
                masterState: {
                    ...prevState.masterState,
                    allFormState: {
                    },
                }
            }
        })
    }

    const onShowOperationPanel = async (operation: string) => {

        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isShowSearchPanel: false,
                isShowOperationPanel: true,
                operation: operation,
                bankerIdAndBankerAcNo: !_.isEmpty(prevState.dynamicOptions.bankCodeDropdownOptions) ? prevState.dynamicOptions.bankCodeDropdownOptions[0].value : "",
                reasonCode: !_.isEmpty(prevState.dynamicOptions.reasonCodeDropdownOptions) ? prevState.dynamicOptions.reasonCodeDropdownOptions[0].value : "",
            }
        })
    }

    const onCloseOperationPanel = async () => {

        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isShowOperationPanel: false,
                reasonCode: "",
                bankerIdAndBankerAcNo: "",
            }
        })
    }

    const onSave = async (entity: RefundEntity, operatingUnit: string) => {
        const valResult = await Validation((operatingUnit !== 'SAPT' && operatingUnit !== 'KICT') ? createRefundValidationSchema : createRefundSaptValidationSchema).ValidateFormOnly(entity);
        let validatedResult: { [x: string]: string } = {};
        if (valResult) {
            validatedResult = { ...valResult };

            refundMainDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }
        return await repo.onSave(entity);
    }

    const onOperation = async (criteria: RefundOperationCriteria) => {
        let msg = "";
        await repo.onOperation(criteria).then(data => {
            if (data && data.success) {
                if (criteria.operation === RefundPermissionCodePolicy.REFUND_BUTTON_PROCESS) {
                    for (const key in data.data) {
                        const dat = data.data;
                        if (dat[key] != null && dat[key] !== undefined) {
                            const tempData = dat[key] as string;

                            let base64 = tempData
                            let bstr = atob(base64)
                            let n = bstr.length
                            let u8arr = new Uint8Array(n)
                            while (n--) {
                                u8arr[n] = bstr.charCodeAt(n)
                            }
                            const blob = new Blob([u8arr], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);

                            window.open(url, '_blank');
                            URL.revokeObjectURL(url);
                        }
                    };
                }
            } else {
                msg = data.data ?? criteria.operation + " failed.";
            }
        });
        return msg;
    }

    const onAddClick = async () => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: true,
                isAdd: true,
                isEdit: false,
            }
        })
    }

    const onEditClick = async () => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isAdd: false,
                isEdit: true,
            }
        })
    }

    const onEditResetClick = async (isAdd: boolean) => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                currentEditRow: isAdd ? { ...EMPTY_REFUND_ENTITY } : prevState.currentRow,
            }
        })
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }

    const onSaveClicked = () => {
        refundMainDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }
    return {
        onSaveClicked: onSaveClicked,
        loadDropdownOption: loadDropdownOption,
        onSearch: onSearch,
        updateSelectedRows: updateSelectedRows,
        onRowDoubleClick: onRowDoubleClick,
        onSearchFieldChange: onSearchFieldChange,
        onDateRangeChange: onDateRangeChange,
        onRemoveAllSearchCriteria: onRemoveAllSearchCriteria,
        onSearchClick: onSearchClick,
        onCloseEditPanel: onCloseEditPanel,
        onShowOperationPanel: onShowOperationPanel,
        onCloseOperationPanel: onCloseOperationPanel,
        onSave: onSave,
        onEditFieldChange: onEditFieldChange,
        onEditClick: onEditClick,
        onAddClick: onAddClick,
        onEditResetClick: onEditResetClick,
        onOperation: onOperation,
        onOperationFieldChange: onOperationFieldChange,
        setIsSearchLoading: setIsSearchLoading,

    }
}